/**
 * Phaser-free aggregation for the Perf readout. One `FrameSample` per rendered frame;
 * `summarizeFrames` turns the recent window into the numbers the Debug panel shows and
 * the `data-perf-*` probes publish.
 */
export type FrameSample = {
  /** Main-thread ms from `PRE_STEP` to `POST_RENDER` (update + render + GL submission). */
  jsMs: number;
  /** GPU ms for the same frame, or `null` when the timer extension is missing / pending. */
  gpuMs: number | null;
  drawCalls: number;
  triangles: number;
  uploadBytes: number;
  fboBinds: number;
};

export type PerfReadout = {
  frames: number;
  fps: number;
  jsMs: number;
  jsP90Ms: number;
  gpuMs: number | null;
  gpuP90Ms: number | null;
  drawCalls: number;
  triangles: number;
  uploadBytes: number;
  fboBinds: number;
};

export const EMPTY_PERF_READOUT: PerfReadout = {
  frames: 0,
  fps: 0,
  jsMs: 0,
  jsP90Ms: 0,
  gpuMs: null,
  gpuP90Ms: null,
  drawCalls: 0,
  triangles: 0,
  uploadBytes: 0,
  fboBinds: 0,
};

function percentile(sorted: number[], fraction: number): number {
  if (sorted.length === 0) {
    return 0;
  }

  const index = Math.min(sorted.length - 1, Math.floor(sorted.length * fraction));
  return sorted[index]!;
}

function round(value: number, digits: number): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

export function summarizeFrames(frames: readonly FrameSample[], fps: number): PerfReadout {
  if (frames.length === 0) {
    return { ...EMPTY_PERF_READOUT, fps: Math.round(fps) };
  }

  const js = frames.map((frame) => frame.jsMs).sort((a, b) => a - b);
  const gpu = frames
    .map((frame) => frame.gpuMs)
    .filter((value): value is number => value !== null)
    .sort((a, b) => a - b);
  const last = frames[frames.length - 1]!;

  return {
    frames: frames.length,
    fps: Math.round(fps),
    jsMs: round(percentile(js, 0.5), 1),
    jsP90Ms: round(percentile(js, 0.9), 1),
    gpuMs: gpu.length > 0 ? round(percentile(gpu, 0.5), 1) : null,
    gpuP90Ms: gpu.length > 0 ? round(percentile(gpu, 0.9), 1) : null,
    drawCalls: last.drawCalls,
    triangles: last.triangles,
    uploadBytes: last.uploadBytes,
    fboBinds: last.fboBinds,
  };
}

export function formatUploadBytes(bytes: number): string {
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_000_000).toFixed(2)} MB`;
  }

  if (bytes >= 1_000) {
    return `${(bytes / 1_000).toFixed(0)} kB`;
  }

  return `${bytes} B`;
}

export function formatMs(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)} ms`;
}
